import { api, generateQueryParamFromObj, prefix } from "@/api";
import { AxiosHeaders, type AxiosRequestConfig } from "axios";
import { serialize } from "object-to-formdata";
import type { userType } from "@/types/user";
import { organizationType } from "@/types/organization";
import { layerType } from "@/types/api";
import { initType } from "@/types/application";

const namespace = "profile";

export const init = (config: AxiosRequestConfig = {}) => {
  return api<initType>({
    url: `${prefix}/happyway/init`,
    method: "get",
    ...config,
  });
};

export const getDetails = (
  params: { withIdentity: boolean; id?: number } = { withIdentity: false },
  config: AxiosRequestConfig = {}
) => {
  return api<userType>({
    url: `${prefix}/${namespace}/profile${params.id ? `/${params.id}` : ""}?withIdentity=${params.withIdentity}`,
    method: "GET",
    ...config,
  });
};

export const getProfileDetail = (id: string, params: any, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<userType & { organization?: Array<organizationType> }>({
    url: `${prefix}/happyway/users/${id}/profile?${query}`,
    method: "GET",
    ...config,
  });
};

export const getRoles = (
  config: AxiosRequestConfig = {}
): Promise<
  Array<{
    applicationId: number;
    roleId: number;
    roleName: string;
    userId: string;
  }>
> => {
  return api({
    url: `${prefix}/${namespace}/profile/roles`,
    method: "get",
    ...config,
  });
};

export const changeRole = (roleId: number, config: AxiosRequestConfig = {}): Promise<any> => {
  return api({
    url: `${prefix}/${namespace}/role/current/${roleId}`,
    method: "PUT",
    ...config,
  });
};

export const login = (config: AxiosRequestConfig = {}): Promise<void> => {
  return api({
    url: `${prefix}/${namespace}/login`,
    method: "POST",
    ...config,
  });
};

export const updateAvatar = (image: File): Promise<void> => {
  const headers = new AxiosHeaders({ "Content-Type": "multipart/form-data" });
  console.log(image);
  return api({
    url: `${prefix}/${namespace}/profile/avatar`,
    method: "post",
    data: serialize({ image: image }),
    headers: headers,
  });
};

export const updateUser = (
  params: Partial<userType>,
  config: AxiosRequestConfig = {}
): Promise<{ id: string }> => {
  return api({
    url: `${prefix}/${namespace}/profile`,
    method: "put",
    data: params,
    ...config,
  });
};

export const getNetwork = (config: AxiosRequestConfig = {}) => {
  return api<any>({
    url: `${prefix}/${namespace}/v2/network`,
    method: "get",
    ...config,
  });
};

export const getListAgoraProfessional = (params: any, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<layerType<userType>>({
    url: `${prefix}/happyway/users/professionals/agora?${query}`,
    method: "get",
    ...config,
  });
};

export const getListAgoraUser = (params: any, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<layerType<userType>>({
    url: `${prefix}/happyway/users/agora?${query}`,
    method: "GET",
    ...config,
  });
};

export const acceptDesigner = (
  id: string,
  params: any,
  config: AxiosRequestConfig = {}
): Promise<{ id: string }> => {
  const query = generateQueryParamFromObj(params);
  return api({
    url: `${prefix}/happyway/users/accept/user/${id}?${query}`,
    method: "put",
    data: {},
    ...config,
  });
};

export const getListProfessionals = (params: any, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<layerType<userType>>({
    url: `${prefix}/happyway/users/professionals?${query}`,
    method: "GET",
    ...config,
  });
};

export const getListSpecialist = (params?: any, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<any>({
    url: `${prefix}/happyway/users/specialists?projections=profile|organization&${query}`,
    method: "GET",
    ...config,
  });
};

export const getListAdministrators = (params?: any, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<any>({
    url: `${prefix}/happyway/users/administrators?projections=profile&${query}`,
    method: "GET",
    ...config,
  });
};

export const getProfessionistAgoraStatus = (params?: any, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<Array<{ code: string; status: string }>>({
    url: `${prefix}/happyway/users/professionals/status?${query}`,
    method: "GET",
    ...config,
  });
};
